import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Stack,
  useMediaQuery,
  useTheme,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Avatar,
  Zoom,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import {
  PersonAdd,
  Email,
  Search,
  TrendingUp,
  CheckCircle,
  ArrowForward,
  CloudUpload,
  Campaign,
  Analytics,
  Rocket,
} from '@mui/icons-material';

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const HowItWorksSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';

  const primaryGradient = isDark
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)'
    : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)';

  const sectionBg = isDark
    ? `linear-gradient(180deg, ${alpha('#0f172a', 0.95)} 0%, ${alpha('#1e1b4b', 0.92)} 100%)`
    : 'linear-gradient(180deg, #f0f9ff 0%, #ffffff 100%)';

  const cardBg = isDark ? alpha('#1e293b', 0.6) : alpha('#ffffff', 0.95);
  const textColor = isDark ? '#f1f5f9' : '#0f172a';
  const accentColor = isDark ? '#818cf8' : '#3b82f6';

  const steps = [
    {
      icon: <PersonAdd />,
      title: 'Sign Up & Setup',
      description: 'Create your account in 30 seconds. Add your email accounts and configure your SMTP settings.',
      details: ['Quick registration', 'Email account setup', 'Team member invitations', 'Initial configuration'],
      color: '#3b82f6',
      number: '01',
    },
    {
      icon: <CloudUpload />,
      title: 'Import or Build Lists',
      description: 'Upload your carrier database or use our search to find and build targeted carrier lists.',
      details: ['CSV import', 'MC range search', 'Filter by location/equipment', 'Build custom lists'],
      color: '#8b5cf6',
      number: '02',
    },
    {
      icon: <Campaign />,
      title: 'Create Campaigns',
      description: 'Design professional email campaigns with our template builder and personalization tools.',
      details: ['Drag-and-drop templates', 'Merge tag personalization', 'Schedule campaigns', 'A/B testing'],
      color: '#ec4899',
      number: '03',
    },
    {
      icon: <Rocket />,
      title: 'Launch & Send',
      description: 'Deploy your campaigns with confidence. Our system ensures optimal deliverability.',
      details: ['Automated sending', 'Email warmup', 'Throttling control', 'Spam score checking'],
      color: '#06b6d4',
      number: '04',
    },
    {
      icon: <Analytics />,
      title: 'Track & Optimize',
      description: 'Monitor performance in real-time. Track opens, clicks, and responses to optimize results.',
      details: ['Real-time analytics', 'Engagement tracking', 'Performance reports', 'ROI measurement'],
      color: '#10b981',
      number: '05',
    },
    {
      icon: <TrendingUp />,
      title: 'Scale & Grow',
      description: 'Expand your operations with team collaboration, automation, and advanced features.',
      details: ['Team management', 'Workflow automation', 'Advanced analytics', 'API integration'],
      color: '#f59e0b',
      number: '06',
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: sectionBg,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: isDark ? 0.08 : 0.04,
          background: `
            radial-gradient(circle at 20% 30%, ${alpha(accentColor, 0.4)} 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, ${alpha('#8b5cf6', 0.4)} 0%, transparent 50%)
          `,
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Stack spacing={2} sx={{ mb: 8, textAlign: 'center' }}>
          <Chip
            label="Simple Process"
            sx={{
              background: primaryGradient,
              color: '#fff',
              fontWeight: 700,
              alignSelf: 'center',
              px: 2,
              py: 2.5,
            }}
          />
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            sx={{
              fontFamily: '"Sora", "Inter", sans-serif',
              fontWeight: 900,
              color: textColor,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            How It Works
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: isDark ? alpha(textColor, 0.7) : alpha(textColor, 0.6),
              maxWidth: 700,
              mx: 'auto',
              lineHeight: 1.8,
            }}
          >
            Get started in minutes and scale to thousands of emails per day with our proven workflow
          </Typography>
        </Stack>

        {/* Steps Grid */}
        <Grid container spacing={4}>
          {steps.map((step, idx) => (
            <Grid item xs={12} md={6} lg={4} key={idx}>
              <Zoom in timeout={300 + idx * 100}>
                <Card
                  sx={{
                    height: '100%',
                    background: cardBg,
                    backdropFilter: 'blur(20px)',
                    borderRadius: 3,
                    border: `1px solid ${alpha(step.color, isDark ? 0.2 : 0.15)}`,
                    boxShadow: `0 8px 32px ${alpha('#000', isDark ? 0.3 : 0.08)}`,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s',
                    '&:hover': {
                      transform: 'translateY(-12px)',
                      boxShadow: `0 20px 60px ${alpha(step.color, 0.3)}`,
                      '& .step-number': {
                        transform: 'scale(1.1)',
                        background: `linear-gradient(135deg, ${step.color} 0%, ${alpha(step.color, 0.7)} 100%)`,
                      },
                      '& .step-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                      },
                    },
                  }}
                >
                  {/* Step Number Badge */}
                  <Box
                    className="step-number"
                    sx={{
                      position: 'absolute',
                      top: -15,
                      right: 20,
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: alpha(step.color, isDark ? 0.2 : 0.15),
                      border: `3px solid ${cardBg}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.4s',
                      boxShadow: `0 4px 20px ${alpha(step.color, 0.3)}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 900,
                        color: step.color,
                        fontFamily: '"Sora", "Inter", sans-serif',
                      }}
                    >
                      {step.number}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 4, pt: 5 }}>
                    <Stack spacing={3}>
                      {/* Icon */}
                      <Box
                        className="step-icon"
                        sx={{
                          width: 70,
                          height: 70,
                          borderRadius: 3,
                          background: alpha(step.color, isDark ? 0.2 : 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.4s',
                          boxShadow: `0 4px 20px ${alpha(step.color, 0.2)}`,
                        }}
                      >
                        {React.cloneElement(step.icon, {
                          sx: { fontSize: 36, color: step.color },
                        })}
                      </Box>

                      {/* Title */}
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: textColor,
                          fontFamily: '"Sora", "Inter", sans-serif',
                        }}
                      >
                        {step.title}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body1"
                        sx={{
                          color: isDark ? alpha(textColor, 0.7) : alpha(textColor, 0.6),
                          lineHeight: 1.8,
                        }}
                      >
                        {step.description}
                      </Typography>

                      {/* Details */}
                      <Stack spacing={1.5}>
                        {step.details.map((detail, i) => (
                          <Stack direction="row" spacing={1.5} alignItems="center" key={i}>
                            <CheckCircle sx={{ fontSize: 20, color: step.color }} />
                            <Typography
                              variant="body2"
                              sx={{
                                color: isDark ? alpha(textColor, 0.8) : alpha(textColor, 0.7),
                                fontWeight: 500,
                              }}
                            >
                              {detail}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>

                      {/* Connector Arrow (not for last item) */}
                      {idx < steps.length - 1 && !isMobile && idx % 3 !== 2 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: -30,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: alpha(step.color, 0.3),
                          }}
                        >
                          <ArrowForward sx={{ fontSize: 32 }} />
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>

        {/* Bottom CTA */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: textColor,
              mb: 2,
            }}
          >
            Ready to streamline your carrier outreach?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: isDark ? alpha(textColor, 0.7) : alpha(textColor, 0.6),
              mb: 4,
            }}
          >
            Join thousands of freight professionals who trust our platform
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorksSection;
